/**
 * Shared evaluation interpretation utility.
 *
 * Single source of truth for converting a raw requestEvaluation object into
 * one of four severity strings: 'safe' | 'caution' | 'danger' | 'neutral'.
 *
 * Import this in every component that needs to display an evaluation status so
 * that the table icon, the approval-table modal, and the ApprovalGuidance card
 * always agree.
 */

/**
 * @param {object|null} evaluation  Raw evaluation object from the API.
 * @returns {'safe'|'caution'|'danger'|'neutral'}
 */
export function interpretEvaluation(evaluation) {
  if (!evaluation || evaluation.error) return 'neutral';

  // Primary path: structured recommended.score string
  const recommended = evaluation.recommended && evaluation.recommended.score;
  if (typeof recommended === 'string' && recommended.length) {
    const s = recommended.toLowerCase();
    if (s === 'approve')          return 'safe';
    if (s.includes('caution'))    return 'caution';
    return 'danger';
  }

  // Legacy path: numeric overall_score_value
  if (typeof evaluation.overall_score_value === 'number') {
    return evaluation.overall_score_value >= 7 ? 'safe' : 'danger';
  }

  // Fallback: evaluation exists but has no actionable score — treat as caution
  if (
    evaluation.summary ||
    evaluation.overall_explanation ||
    Array.isArray(evaluation.all_results)
  ) {
    return 'caution';
  }

  return 'neutral';
}

/** Maps a severity string to a human-readable badge label. */
export function severityToLabel(severity) {
  if (severity === 'safe')    return 'PASS';
  if (severity === 'caution') return 'CAUTION';
  if (severity === 'danger')  return 'FAIL';
  return 'N/A';
}

/** Maps a severity string to a Font Awesome icon class. */
export function severityToIcon(severity) {
  if (severity === 'safe')    return 'fa-solid fa-check';
  if (severity === 'caution') return 'fa-solid fa-triangle-exclamation';
  if (severity === 'danger')  return 'fa-solid fa-xmark';
  return 'fa-solid fa-minus';
}

/** Maps a severity string to the CSS modifier used by both components. */
export function severityToClass(severity) {
  if (severity === 'safe')    return 'safe';
  if (severity === 'caution') return 'caution';
  if (severity === 'danger')  return 'danger';
  return 'muted';
}